import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import classNames from 'classnames';
import { List, Record } from 'immutable';
import _ from 'lodash';

import { BET_SOURCES } from '@xanadu/ui-shared/constants/unsubmittedOffers';
import { DEFAULT_BONUS_DROPDOWN_OPTIONS } from '@xanadu/ui-shared/constants';
import { getPlatform } from '@xanadu/ui-shared/interfaces/googleTagManager/api';
import { isNumber } from '@xanadu/ui-shared/utils/numbers';
import usePrevious from '@xanadu/ui-shared/hooks/usePrevious';

import { ERROR_MESSAGES } from '../../../constants';

import helpers from '../../../../javascripts/helpers';
import { currency as localiseCurrency } from '../../../utils/localise';
import { getCustomBetError, getRealOfferStatus } from '../utils';


import NewCashout from '../../../containers/betslip/NewCashout';
import BetFeedback from '../BetFeedback/BetFeedback';
import OfferInfo from './OfferInfo/OfferInfo';

import IconClose from '../../../../images/icons/misc/icon-close.svg';

import styles from './Offer.module.scss';

const SHOW_BONUS_THRESHHOLD = 0.1;

function stringIsValidNumber(str) {
  if (str === '') {
    return true;
  }
  return /^[-+.\d][.\d]*$/.test(str);
}

function firstNonNull(...args) {
  // eslint-disable-next-line no-restricted-syntax
  for (const arg of args) {
    if (arg !== null) {
      return arg;
    }
  }

  return 0;
}

const CASH_BET_OPTION = DEFAULT_BONUS_DROPDOWN_OPTIONS[0].name;

const Offer = ({
  isActiveOffer,
  onUpdateOffer,
  onRemoveOffer,
  onRemoveMultipleSelection,
  onSubmitOffer,
  onUseBonusUpdate,
  offer,
  aggregationRule,
  bonusAllowed,
  offerIndex,
  eventStatus,
  eventInRunning,
  marketWillBeLive,
  marketStatus,
  bonusBalance,
  freeBets,
  isLoggedIn,
  showBetConfirmation,
  cashoutAvailable,
  difference,
  currency,
  oddsType,
  selectRunner,
  onCancelOffer,
  onCancelUnmatchedOffers,
  handleActiveTabLink,
  updateSingleUnmatchedOfferByUser,
  onSubmitUnmatched,
  bestPrice,
  subscribeToMarket,
  unsubscribeToMarket,
  subscribeToEvent,
  unsubscribeToEvent,
  isMobile,
  isBetBuilderRestricted
}) => {
  const computedBonusDropdownOptions = useMemo(() => {
    if (freeBets && freeBets.size) {
      return [
        ...DEFAULT_BONUS_DROPDOWN_OPTIONS,
        ...freeBets.map(({ id, amount, currency: curr, name, products }) => {
          return {
            id,
            value: `${localiseCurrency(amount, { symbol: false })}`,
            name: `${localiseCurrency(amount, curr, { symbol: true })} ${name}`,
            products,
          };
        })
      ];
    }
    return DEFAULT_BONUS_DROPDOWN_OPTIONS;
  }, [freeBets]);

  const getOptionValue = useMemo(() => ((offer.useBonus && freeBets.size) &&
    computedBonusDropdownOptions
      .find(bonus => bonus.id === offer.bonusId)?.name || CASH_BET_OPTION), [offer, freeBets.size]);

  const [unmatchedData, setUnmatchedData] = useState(null);
  const prevUnmatchedData = usePrevious(unmatchedData);
  const [countdown, setCountdown] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const [chooseKeep, setChooseKeep] = useState(!!offer.keepInPlay);
  const [showCashout, setShowCashout] = useState(false);
  const [cashoutSubmitted, setCashoutSubmitted] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [newBetBuilderOdds, setNewBetBuilderOdds] = useState(null);
  const [prevBetBuilderOdds, setPrevBetBuilderOdds] = useState(null);
  const [betBuilderLegsWithErrors, setBetBuilderLegsWithErrors] = useState([]);
  const [optionValue, setOptionValue] = useState(getOptionValue);
  const tickInterval = useRef(null);
  const refOdds = useRef(null);
  const refInputStake = useRef(null);
  const refButton = useRef(null);
  const [lastEditedInput, setLastEditedInput] = useState('back');
  const oddsLadder = useMemo(() => helpers.oddsLadder, [helpers]);
  const prevBonusBalance = usePrevious(bonusBalance);
  const {
    useBonus,
    source,
    oddsLocked,
  } = offer;
  const { processingState, status, error, delay, editTime } = getRealOfferStatus(offer) || {};
  const prevOddsLocked = usePrevious(oddsLocked);

  const isNew = !offer.id;
  const isUnsubmittedType = offer.type === 'unsubmitted';
  const isSubmitting = processingState === 'submitting';
  const isBetBuilderBet = source === BET_SOURCES.betBuilder;

  const failedProcessingState = processingState === 'failed';

  const showBetBuilderOddsAcceptance = failedProcessingState && Boolean(newBetBuilderOdds);

  const unmatchedBet = unmatchedData
    ? offer.unmatched.find((unmatch) => unmatch.offerId === unmatchedData)
    : null;
  const getBet = () => {
    const unmatchedOfferData = unmatchedData
      ? offer.unmatched.find(({ offerId }) => offerId === unmatchedData)
      : null;

    const computedOffer = unmatchedOfferData || offer;

    const { remote = {} } = computedOffer;

    let odds = firstNonNull(computedOffer.odds, remote.odds);
    if (computedOffer.odds === '') {
      odds = '';
    }

    const back = firstNonNull(computedOffer.backersStake, remote.backersStake);
    const lay = firstNonNull(computedOffer.layersStake, remote.layersStake);

    const oddsStr = typeof odds === 'undefined' ? '' : String(odds);
    let adjustedOdds = oddsStr;

    if (oddsStr !== 'undefined' && oddsStr.indexOf('.') > 0) {
      const decimalPlaces = oddsStr.length - oddsStr.indexOf('.') - 1;
      const isUserTyping = decimalPlaces <= 5 && ['.', '0'].includes(oddsStr.at(-1));
      if (!isUserTyping) {
        adjustedOdds = helpers.localise.odds(parseFloat(oddsStr, 10), {
          side: offer.side
        }).toString();
      }
    }

    const computedBack = isNumber(back) ? back.toFixed(2) : back;
    const computedLay = isNumber(lay) ? lay.toFixed(2) : lay;

    const bet = {
      back: parseFloat(back) === 0 ? back : computedBack,
      lay: parseFloat(lay) === 0 ? lay : computedLay,
      odds,
      adjustedOdds,
      id: offer.id,
      cid: offer.cid,
      side: offer.side,
      useBonus: offer.useBonus,
      ...(isBetBuilderBet && { decimalOdds: offer.decimalOdds })
    };

    return bet;
  };

  const betData = getBet();
  // only show first message
  const firstError = useMemo(() => (
    error && error.length ? error[0] : null
  ), [error]);

  let errorMessage = '';
  let messageIconSource = '';
  let customErrorMessage = false;

  if (firstError && failedProcessingState && !newBetBuilderOdds) {
    messageIconSource = '/assets/images/icons/misc/alert.svg';

    errorMessage = firstError.messages[0];
    customErrorMessage = isUnsubmittedType && getCustomBetError(firstError);

    if (customErrorMessage) {
      errorMessage = customErrorMessage;

      if (customErrorMessage === ERROR_MESSAGES.UNAUTHORIZED_ACCESS[0]) {
        errorMessage = 'Please Log in';
      }
    }
  }

  const betBuilderWithErrors = showBetBuilderOddsAcceptance
    || errorMessage.includes('Max stake is')
    || [
      ERROR_MESSAGES.ODDS_NOT_AVAILABLE[0],
      ERROR_MESSAGES.INVALID_LEGS[0],
      ERROR_MESSAGES.NOT_AVAILABLE_IN_PLAY[0],
      ERROR_MESSAGES.EVENT_MUST_BE_OPEN[0],
      ERROR_MESSAGES.ODDS_TYPE_MISMATCH[0],
      ERROR_MESSAGES.BET_BUILDER_RESTRICTED_IE[0],
    ].includes(errorMessage);

  const focusOddsInput = () => {
    const platform = getPlatform();
    let el;

    if (!offer.id) {
      if (!offer.odds) {
        el = refOdds.current;
      } else {
        el = refInputStake.current;
      }

      if (el) {
        if (platform === 'desktop') {
          el.focus();
        } else {
          setTimeout(() => {
            el.focus();
          }, 500);
        }
      }
    }
  };

  const calculateUpdate = (field = 'odds', value) => {
    // NOTE: field can be one of 3 strings odds|back|lay
    const oddsConversion = helpers.oddsConversion;

    let decimalOdds;
    if (oddsType !== 'DECIMAL') {
      decimalOdds = oddsConversion((field === 'odds' ? value : betData.odds), oddsType, 'DECIMAL');

      if (isBetBuilderBet) {
        decimalOdds = betData.decimalOdds;
      }
    } else {
      decimalOdds = (field === 'odds' ? value : betData.odds);
    }

    const parsedDecimalOdds = parseFloat(decimalOdds);

    switch (field) {
      case 'odds': {
        const computedData = { odds: value };

        const { side, back, lay, useBonus: useBonusData } = betData;

        const computedBack = lay && Number(value) !== 1 ? (parseFloat(lay) / (parsedDecimalOdds - 1)) : 0;
        const computedLay = back && Number(value) !== 1
          ? (parseFloat(back) * (parsedDecimalOdds - 1))
          : 0;

        const backUpdate = computedBack.toFixed(2);
        const layUpdate = computedLay.toFixed(2);

        if (side === 'lay' && useBonusData) {
          computedData.back = isNaN(backUpdate) || parseFloat(backUpdate) === 0 ? '' : backUpdate;
          computedData.lay = lay || '';

          return computedData;
        }

        if (lastEditedInput === 'back') {
          computedData.back = back || '';
          computedData.lay = isNaN(layUpdate) || parseFloat(layUpdate) === 0 ? '' : layUpdate;
        } else {
          computedData.back = isNaN(backUpdate) || parseFloat(backUpdate) === 0 ? '' : backUpdate;
          computedData.lay = lay || '';
        }

        return computedData;
      }
      case 'back': {
        const lay = (value * (parsedDecimalOdds - 1)).toFixed(2) || 0;

        return {
          back: value,
          lay: isNaN(lay) || parseFloat(lay) === 0 ? '' : lay,
          odds: betData.odds,
        };
      }
      case 'lay': {
        const back = (value / (parsedDecimalOdds - 1)).toFixed(2) || 0;

        return {
          back: isNaN(back) || parseFloat(back) === 0 ? '' : back,
          lay: value,
          odds: betData.odds,
        };
      }
      default: {
        // eslint-disable-next-line no-console
        console.warn(`${field} was not a valid field`);
        return betData;
      }
    }
  };

  const updateOdds = (newOddsValue, options = {}) => {
    let odds = newOddsValue;

    const newOdds = oddsLadder(oddsType);
    const exchangeType = 'back-lay';
    let valid = true;
    if (stringIsValidNumber(odds) && odds !== '') {
      if (options.quantise && !isBetBuilderBet) {
        odds = newOdds.quantise(odds, exchangeType, offer.side === 'lay');
      }
    } else {
      valid = false;
    }

    const update = calculateUpdate('odds', odds);

    if (unmatchedData) {
      updateSingleUnmatchedOfferByUser({
        id: unmatchedData,
        backersStake: update.back,
        layersStake: update.lay,
        odds: update.odds,
        valid
      });
    } else {
      onUpdateOffer({
        cid: offer.cid,
        id: offer.id,
        backersStake: update.back,
        layersStake: update.lay,
        odds,
        valid
      });
    }
  };

  const updateBack = (back) => {
    const update = calculateUpdate('back', back);

    if (unmatchedData) {
      updateSingleUnmatchedOfferByUser({
        id: unmatchedData,
        backersStake: back,
        layersStake: update.lay,
        odds: update.odds,
      });
    } else {
      onUpdateOffer({
        cid: offer.cid,
        id: offer.id,
        backersStake: back,
        layersStake: update.lay,
        odds: update.odds,
      });
    }

    setLastEditedInput('back');
  };

  const updateLay = (lay) => {
    const update = calculateUpdate('lay', lay);

    if (unmatchedData) {
      updateSingleUnmatchedOfferByUser({
        id: unmatchedData,
        backersStake: update.back,
        layersStake: lay,
        odds: update.odds,
      });
    } else {
      onUpdateOffer({
        cid: offer.cid,
        id: offer.id,
        backersStake: update.back,
        layersStake: lay,
        odds: update.odds,
      });
    }

    setLastEditedInput('lay');
  };

  const reset = () => {
    if (processingState !== 'unsubmitted') {
      const { cid, id } = offer;

      if (unmatchedData) {
        updateSingleUnmatchedOfferByUser({
          id: unmatchedData,
          backersStake: null,
          layersStake: null,
          odds: null
        });
      } else {
        onUpdateOffer({
          cid,
          id,
          backersStake: null,
          layersStake: null,
          odds: null
        });
      }
    }
  };

  const tick = (offerProcessingState, offerEditTime, offerDelay) => {
    if (offerProcessingState === 'submitting') {
      const now = Date.now().valueOf();
      const deadline = new Date(offerEditTime).valueOf() + (offerDelay * 1000);
      const newcountdown = Math.max(0, Math.round(((deadline - now) + 500) / 1000));
      if (newcountdown !== countdown) {
        setCountdown(newcountdown);
      }
      tickInterval.current = setTimeout(() => (
        tick(offerProcessingState, offerEditTime, offerDelay)
      ), 100);
    }
  };

  const handleUseBonus = ({ id: bonusId, value, name }) => {
    setOptionValue(name);
    const offerPayload = {
      cid: offer.cid,
      id: offer.id,
      useBonus: value !== 'NONE',
      ...(value !== 'NONE') ? { bonusId } : { bonusId: null }
    };

    if ((['back', 'win'].includes(offer.side)) && value !== 'NONE') {
      updateBack(value);
      onUpdateOffer({
        ...offerPayload,
        backersStake: value
      });
    } else if ((['lay', 'lose'].includes(offer.side)) && value !== 'NONE') {
      updateLay(value);
      onUpdateOffer({
        ...offerPayload,
        layersStake: value
      });
    } else {
      onUpdateOffer({
        ...offerPayload,
      });
    }

    if (!offerPayload.useBonus) {
      onUseBonusUpdate(offer.cid);
    }
  };

  const updateOddsLockStatus = () => {
    onUpdateOffer({
      cid: offer.cid,
      id: offer.id,
      oddsLocked: !oddsLocked
    });
  };

  const handleSubmit = async () => {
    const { cid, id } = offer;
    const { adjustedOdds, back } = betData;
    const isOpen = offer.type === 'open';

    if (
      countdown > 0
      || (adjustedOdds === '' || back === '' || back === undefined)
      || betBuilderWithErrors
      || processingState === 'submitting'
    ) {
      return;
    }

    if (isUnsubmittedType && ['unsubmitted', 'failed', 'flushed'].includes(status)) {
      onSubmitOffer([{ cid }]);
    }

    if (unmatchedData) {
      onSubmitUnmatched(unmatchedData);
    }

    if (isOpen && !unmatchedData && ['open', 'edited', 'flushed', 'failed'].includes(status)) {
      onSubmitOffer([{ id }]);
    }

    if (confirmed) {
      setConfirmed(false);
    }
  };

  const handleKeepInPlay = (value) => {
    setChooseKeep(value);
    onUpdateOffer({
      cid: offer.cid,
      id: offer.id,
      keepInPlay: value,
    });
  };

  const handleCloseButtonClick = () => {
    if (!isSubmitting) {
      onRemoveMultipleSelection([offer.cid]);
      onRemoveOffer([offer.cid]);
    }
  };

  const acceptNewBetBuilderOdds = () => {
    const {
      newOdds,
    } = offer;

    if (newOdds) {
      onUpdateOffer({
        cid: offer.cid,
        id: offer.id,
        error: [],
        processingState: '',
      });

      setNewBetBuilderOdds(null);
      setPrevBetBuilderOdds(null);
    }
  };

  const switchToCashBet = () => {
    setOptionValue(CASH_BET_OPTION);
    onUpdateOffer({
      cid: offer.cid,
      id: offer.id,
      useBonus: !offer.useBonus,
      bonusId: null,
    });
  };

  useEffect(() => {
    const age = new Date().valueOf() - new Date(offer.createdAt).valueOf();
    const isJustMounted = (offer.createdAt && (age < 1000));

    tick();

    if (isJustMounted) {
      focusOddsInput();
      updateOdds(offer.odds, { quantise: true });
    }
  }, [offer.createdAt]);

  useEffect(() => {
    if (bonusBalance !== prevBonusBalance &&
      prevBonusBalance >= SHOW_BONUS_THRESHHOLD &&
      useBonus) {
      const bonusAvailable = freeBets.find(bet => bet.id === offer.bonusId);

      if (!bonusAvailable) {
        switchToCashBet();
      }
    }
  }, [prevBonusBalance, bonusBalance, offer.cid, offer.id, useBonus, freeBets]);

  useEffect(() => {
    const bonusId = offer.bonusId;
    if (useBonus && bonusId) {
      const bonusAvailable = freeBets.find(bet => bet.id === bonusId);

      if (!bonusAvailable) {
        switchToCashBet();
      }
    }
  }, [useBonus, offer.bonusId]);

  useEffect(() => {
    if (processingState === 'submitting') {
      tick(processingState, editTime, delay);
    }
    if (processingState === 'submitted') {
      setEditModal(false);

      setTimeout(() => {
        setUnmatchedData('');
      }, 2000); // waiting feedback message to disappear
    }
    if (processingState === '') {
      setEditModal(false);
    }
    return () => {
      clearTimeout(tickInterval.current);
    };
  }, [processingState, editTime, delay]);

  useEffect(() => {
    if (
      prevUnmatchedData
      && unmatchedData
      && prevUnmatchedData !== unmatchedData
    ) {
      reset();
    }
  }, [unmatchedData, prevUnmatchedData]);

  useEffect(() => {
    if (!cashoutSubmitted) {
      setShowCashout(false);
    }
  }, [cashoutSubmitted]);

  useEffect(() => {
    if (!isLoggedIn) {
      const offerPayload = {
        cid: offer.cid,
        id: offer.id,
        useBonus: false,
      };

      onUpdateOffer({
        ...offerPayload,
      });
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (oddsLocked) {
      if (bestPrice && bestPrice.odds) {
        updateOdds(bestPrice.odds, { quantise: true });
      }

      if (!bestPrice) {
        onUpdateOffer({
          cid: offer.cid,
          odds: null
        });
      }
    }
  }, [bestPrice, oddsLocked, offer.cid]);

  useEffect(() => {
    if (confirmed) {
      onUpdateOffer({
        cid: offer.cid,
        oddsLocked: false
      });
    }
  }, [confirmed, offer.cid]);

  useEffect(() => {
    if (isUnsubmittedType) {
      if (!prevOddsLocked && oddsLocked) {
        _.debounce(() => {
          subscribeToEvent(offer.eventId);
          subscribeToMarket(offer.marketId);
        }, 400)();
      }
    }
  }, [offer.marketId, offer.eventId, oddsLocked, prevOddsLocked, isUnsubmittedType]);

  const unsubscribeOfferData = () => {
    if (isUnsubmittedType && offer.oddsLocked) {
      _.debounce(() => {
        unsubscribeToEvent(offer.eventId);
        unsubscribeToMarket(offer.marketId);
      }, 400)();
    }
  };

  useEffect(() => () => {
    unsubscribeOfferData();
  }, [offer.oddsLocked]);

  useEffect(() => {
    if (firstError && isBetBuilderBet) {
      if (offer.newOdds) {
        setPrevBetBuilderOdds(offer.odds);
        setNewBetBuilderOdds(offer.newOdds);
        updateOdds(offer.newOdds);
      }

      if (offer.invalidLegs) {
        setBetBuilderLegsWithErrors(offer.invalidLegs.toArray());
      }
    }
  }, [firstError, offer.newOdds, offer.invalidLegs, isBetBuilderBet]);

  useEffect(() => {
    if (isBetBuilderBet && isUnsubmittedType) {
      if (isBetBuilderRestricted) {
        onUpdateOffer({
          cid: offer.cid,
          processingState: 'failed',
          error: [{
            messages: [ERROR_MESSAGES.BET_BUILDER_RESTRICTED_IE[0]]
          }]
        });

        return;
      }
      if (eventInRunning) {
        onUpdateOffer({
          cid: offer.cid,
          id: offer.id,
          processingState: 'failed',
          error: [{
            messages: [ERROR_MESSAGES.NOT_AVAILABLE_IN_PLAY[0]]
          }]
        });

        return;
      }

      if (['suspended', 'closed'].includes(eventStatus)) {
        onUpdateOffer({
          cid: offer.cid,
          id: offer.id,
          processingState: 'failed',
          error: [{
            messages: [ERROR_MESSAGES.EVENT_MUST_BE_OPEN[0]]
          }]
        });

        return;
      }

      if (offer.oddsType !== oddsType) {
        onUpdateOffer({
          cid: offer.cid,
          processingState: 'failed',
          error: [{
            messages: [ERROR_MESSAGES.ODDS_TYPE_MISMATCH[0]]
          }]
        });

        return;
      }

      onUpdateOffer({
        cid: offer.cid,
        id: offer.id,
        processingState: '',
        error: []
      });
    }
  }, [
    oddsType,
    offer.cid,
    eventStatus,
    offer.oddsType,
    eventInRunning,
    isBetBuilderBet,
    isUnsubmittedType,
    offer.decimalOdds,
    offer.backersStake,
    isBetBuilderRestricted
  ]);

  const offerContainer = classNames({
    [styles.offerContainer]: true,
    [styles[`offerContainer--${offer.status}--${offer.side}`]]: true,
    [styles['offerContainer--cashout']]: cashoutSubmitted,
  });

  const verticalBar = classNames({
    [styles.verticalBar]: true,
    [styles[`verticalBar--${offer.side}`]]: !isBetBuilderBet,
    [styles['verticalBar--betBuilder']]: isBetBuilderBet,
  });

  if (processingState === 'submitted' && !unmatchedData) {
    return (
      <BetFeedback
        offer={offer}
        handleActiveTabLink={handleActiveTabLink}
      />
    );
  }

  if (processingState === 'submitted' && unmatchedData) {
    return (
      <BetFeedback
        offer={offer}
        unmatchedBet={unmatchedBet}
        handleActiveTabLink={handleActiveTabLink}
      />
    );
  }

  const messageContainerClass = classNames({
    [styles.messageContainer]: true,
    [styles['messageContainer--customErrorMessage']]: customErrorMessage
  });

  const errorMessageClass = classNames({
    [styles.errorMessage]: true,
    [styles['errorMessage--custom']]: customErrorMessage,
    [styles['errorMessage--hide']]: showBetBuilderOddsAcceptance,
  });

  return (
    <div
      className={offerContainer}
      data-driver={isActiveOffer ? `open-offer-${offerIndex}` : `offer-${offerIndex}`}
    >
      <div className={verticalBar}>
        {isUnsubmittedType && (
          <button
            className={styles.closeButton}
            onClick={handleCloseButtonClick}
            title="Remove Bet"
          >
            <IconClose />
          </button>
        )}
        <div className={styles.verticalTitle}>
          {isBetBuilderBet ? 'BET BUILDER' : offer.side}
        </div>
      </div>
      <div className={styles.offer}>
        <OfferInfo
          offer={offer}
          editModal={editModal}
          setEditModal={setEditModal}
          showCashout={showCashout}
          aggregationRule={aggregationRule}
          oddsType={oddsType}
          setConfirmed={setConfirmed}
          onCancelOffer={onCancelOffer}
          onCancelUnmatchedOffers={onCancelUnmatchedOffers}
          setUnmatchedData={setUnmatchedData}
          unmatchedData={unmatchedData}
          oddsLadder={oddsLadder}
          offerIndex={offerIndex}
          refInputStake={refInputStake}
          refOdds={refOdds}
          betData={betData}
          confirmed={confirmed}
          currency={currency}
          isNew={isNew}
          updateBack={updateBack}
          updateLay={updateLay}
          updateOdds={updateOdds}
          reset={reset}
          handleSubmit={handleSubmit}
          stringIsValidNumber={stringIsValidNumber}
          onRemoveMultipleSelection={onRemoveMultipleSelection}
          processingState={processingState}
          onRemoveOffer={onRemoveOffer}
          countdown={countdown}
          showBetConfirmation={showBetConfirmation}
          cashoutAvailable={cashoutAvailable}
          marketStatus={marketStatus}
          selectRunner={selectRunner}
          setShowCashout={setShowCashout}
          eventInRunning={eventInRunning}
          isLoggedIn={isLoggedIn}
          marketWillBeLive={marketWillBeLive}
          handleKeepInPlay={handleKeepInPlay}
          bonusBalance={bonusBalance}
          chooseKeep={chooseKeep}
          bonusAllowed={bonusAllowed}
          handleUseBonus={handleUseBonus}
          optionValue={optionValue}
          bonusDropdownOptions={computedBonusDropdownOptions}
          updateOddsLockStatus={updateOddsLockStatus}
          oddsLocked={oddsLocked}
          lastEditedInput={lastEditedInput}
          isUnsubmittedType={isUnsubmittedType}
          isBetBuilderBet={isBetBuilderBet}
          betBuilderLegsWithErrors={betBuilderLegsWithErrors}
          betBuilderWithErrors={betBuilderWithErrors}
          isMobile={isMobile}
          showBetBuilderOddsAcceptance={showBetBuilderOddsAcceptance}
          acceptNewBetBuilderOdds={acceptNewBetBuilderOdds}
          newBetBuilderOdds={newBetBuilderOdds}
          prevBetBuilderOdds={prevBetBuilderOdds}
        />
        {showCashout && (
          <NewCashout
            difference={difference}
            side={offer.side}
            runnerId={offer.runnerId}
            offer={offer}
            isNew={isNew}
            offerIndex={offerIndex}
            eventInRunning={eventInRunning}
            setShowCashout={setShowCashout}
            isLoggedIn={isLoggedIn}
            selectRunner={selectRunner}
            setCashoutSubmitted={setCashoutSubmitted}
            cashoutSubmitted={cashoutSubmitted}
            refbutton={refButton}
          />
        )}
        {!!(error && error.length) && (
          <div className={errorMessageClass}>
            <img
              alt="Info"
              className={styles.messageRowIcon}
              src={messageIconSource}
              role="presentation"
            />
            {Boolean(errorMessage) && (
              <div className={messageContainerClass}>
                <span className={styles.messageContent}>
                  {errorMessage}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

Offer.propTypes = {
  onUpdateOffer: PropTypes.func,
  onRemoveOffer: PropTypes.func,
  updateSingleUnmatchedOfferByUser: PropTypes.func.isRequired,
  onSubmitUnmatched: PropTypes.func,
  onRemoveMultipleSelection: PropTypes.func,
  onSubmitOffer: PropTypes.func.isRequired,
  onUseBonusUpdate: PropTypes.func,
  offer: PropTypes.instanceOf(Record).isRequired,
  offerIndex: PropTypes.number,
  eventInRunning: PropTypes.bool,
  isActiveOffer: PropTypes.bool,
  eventStatus: PropTypes.string.isRequired,
  bonusBalance: PropTypes.number,
  freeBets: PropTypes.instanceOf(List),
  isLoggedIn: PropTypes.bool,
  onCancelOffer: PropTypes.func,
  cashoutAvailable: PropTypes.bool.isRequired,
  difference: PropTypes.number.isRequired,
  marketStatus: PropTypes.string,
  aggregationRule: PropTypes.string,
  showBetConfirmation: PropTypes.bool.isRequired,
  bonusAllowed: PropTypes.bool.isRequired,
  marketWillBeLive: PropTypes.bool,
  isBetBuilderRestricted: PropTypes.bool,
  onCancelUnmatchedOffers: PropTypes.func,
  selectRunner: PropTypes.func.isRequired,
  subscribeToMarket: PropTypes.func.isRequired,
  unsubscribeToMarket: PropTypes.func.isRequired,
  subscribeToEvent: PropTypes.func.isRequired,
  unsubscribeToEvent: PropTypes.func.isRequired,
  handleActiveTabLink: PropTypes.func,
  currency: PropTypes.string,
  oddsType: PropTypes.string.isRequired,
  isMobile: PropTypes.bool.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }),
  bestPrice: PropTypes.shape({
    odds: PropTypes.number.isRequired
  }),
};

export default Offer;
